import { CloseOutlined, MenuOutlined } from "@ant-design/icons";
import { Drawer } from "antd";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ROUTES } from "../core/navigation";
import { T, rgba } from "../lib/theme";
import { useMetaContext } from "../core/contexts/MetaContextProvider";

const NAV_LINKS = [
  { label: "Features", href: "/features" },
  { label: "Pricing", href: "/pricing" },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Contact", href: "/contact" },
];

export default function Nav({ scrolled }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const { navigateToPage, navigateToPetPulseApp } = useMetaContext();

  const logoEl = (
    <Link
      to={ROUTES.home}
      style={{
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
      onClick={() => setOpen(false)}
    >
      <img src="/logo_min.png" alt="logo" />
    </Link>
  );

  return (
    <>
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          padding: "0 clamp(1rem, 4vw, 2rem)",
          height: 66,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: scrolled
            ? "rgba(255,255,255,0.94)"
            : "rgba(255,255,255,0.82)",
          borderBottom: `1px solid ${scrolled ? T.border : "transparent"}`,
          backdropFilter: "blur(18px)",
          transition: "all 0.3s ease",
        }}
      >
        {logoEl}

        <div
          style={{ display: "flex", gap: "1.8rem", alignItems: "center" }}
          className="hide-mobile"
        >
          {NAV_LINKS.map((l) => {
            const active = location.pathname === l.href;
            return (
              <Link
                key={l.label}
                to={l.href}
                style={{
                  color: active ? T.accentDark : T.textMuted,
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  fontWeight: active ? 700 : 500,
                  borderBottom: `2px solid ${
                    active ? T.accent : "transparent"
                  }`,
                  paddingBottom: 2,
                  transition: "color 0.2s, border-color 0.2s",
                  fontFamily: T.fonts.body,
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.color = T.textHead;
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.color = T.textMuted;
                }}
              >
                {l.label}
              </Link>
            );
          })}
        </div>

        <div
          style={{ display: "flex", gap: 10, alignItems: "center" }}
          className="hide-mobile"
        >
          <button
            style={{
              background: "transparent",
              border: "none",
              color: T.textMuted,
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer",
              padding: "8px 14px",
              fontFamily: T.fonts.body,
              transition: "color 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = T.textHead)}
            onMouseLeave={(e) => (e.currentTarget.style.color = T.textMuted)}
            onClick={() => navigateToPage(ROUTES.getStarted)}
          >
            Get Started
          </button>
          <button
            onClick={navigateToPetPulseApp}
            className="pulse-glow"
            style={{
              background: `linear-gradient(135deg, ${T.accent}, ${T.accentDark})`,
              border: "none",
              borderRadius: 9,
              color: "#fff",
              fontSize: "0.875rem",
              fontWeight: 700,
              cursor: "pointer",
              padding: "9px 22px",
              fontFamily: T.fonts.body,
              transition: "transform 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "scale(1.04)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            Open App
          </button>
        </div>

        <button
          className="show-mobile"
          onClick={() => setOpen(true)}
          style={{
            background: "transparent",
            border: `1.5px solid ${T.border}`,
            borderRadius: 9,
            padding: "7px 10px",
            cursor: "pointer",
            display: "none",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MenuOutlined style={{ fontSize: 18, color: T.textHead }} />
        </button>
      </nav>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        placement="right"
        width={280}
        closeIcon={<CloseOutlined style={{ color: T.textHead }} />}
        title={logoEl}
        styles={{
          header: {
            borderBottom: `1px solid ${T.border}`,
            padding: "14px 20px",
          },
          body: {
            padding: "1.5rem 1.2rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
          },
        }}
      >
        {NAV_LINKS.map((l) => {
          const active = location === l.href;
          return (
            <Link
              key={l.label}
              to={l.href}
              onClick={() => setOpen(false)}
              style={{
                display: "block",
                padding: "12px 14px",
                borderRadius: 10,
                textDecoration: "none",
                fontFamily: T.fonts.body,
                fontSize: "0.95rem",
                fontWeight: active ? 700 : 500,
                color: active ? T.accentDark : T.textBody,
                background: active ? T.accentLight : "transparent",
                transition: "background 0.2s",
              }}
            >
              {l.label}
            </Link>
          );
        })}
        <div
          style={{
            marginTop: "auto",
            paddingTop: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <button
            style={{
              border: `1.5px solid ${T.borderMid}`,
              borderRadius: 10,
              background: "transparent",
              color: T.textBody,
              padding: "11px",
              fontFamily: T.fonts.body,
              fontWeight: 600,
              fontSize: "0.9rem",
              cursor: "pointer",
            }}
            onClick={() => navigateToPage(ROUTES.getStarted)}
          >
            Get Started
          </button>
          <button
            style={{
              background: `linear-gradient(135deg, ${T.accent}, ${T.accentDark})`,
              border: "none",
              borderRadius: 10,
              color: "#fff",
              padding: "12px",
              fontFamily: T.fonts.body,
              fontWeight: 700,
              fontSize: "0.9rem",
              cursor: "pointer",
              boxShadow: `0 4px 16px ${rgba(T.accent, 0.4)}`,
            }}
            onClick={navigateToPetPulseApp}
          >
            Open App
          </button>
        </div>
      </Drawer>

      <style>{`
        @media (max-width: 768px) {
          .hide-mobile { display: none !important; }
          .show-mobile { display: flex !important; }
        }
      `}</style>
    </>
  );
}
